import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import { getAuditLogs, getAuditSummary } from '../services/api';
import { toast } from 'sonner';
import {
  Search, Filter, Clock, Globe2, User, Shield,
  ChevronLeft, ChevronRight, Activity, Loader2,
  Briefcase, Users, Video, Bell, Trash2, RotateCcw,
  ArrowLeftRight, Pencil, Plus, Link, FileText, Eye
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const ACTION_ICONS = {
  create: { icon: Plus, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  edit: { icon: Pencil, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  soft_delete: { icon: Trash2, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  permanent_delete: { icon: Trash2, color: 'text-red-500', bg: 'bg-red-500/10' },
  restore: { icon: RotateCcw, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  move_region: { icon: ArrowLeftRight, color: 'text-violet-500', bg: 'bg-violet-500/10' },
  generate_link: { icon: Link, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  start_interview: { icon: Video, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  complete_interview: { icon: Video, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  mark_read: { icon: Eye, color: 'text-gray-500', bg: 'bg-gray-500/10' },
  merge: { icon: FileText, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
};

const ENTITY_ICONS = {
  job: Briefcase,
  candidate: Users,
  interview: Video,
  notification: Bell,
};

export default function AuditLogPage() {
  const { region, regionLabel } = useRegion();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, today: 0 });
  const [actionTypes, setActionTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { region, limit: pageSize, skip: page * pageSize };
      if (search) params.search = search;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [data, sum] = await Promise.all([
        getAuditLogs(params),
        getAuditSummary(region),
      ]);

      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setActionTypes(data.action_types || []);
      setEntityTypes(data.entity_types || []);
      setSummary(sum);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [region, search, actionFilter, entityFilter, dateFrom, dateTo, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / pageSize);

  const formatAction = (action) => action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Audit Log
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {regionLabel} &middot; Complete activity trail for security and compliance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Card className="px-4 py-2">
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{summary.total}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Total Events</p>
              </div>
              <div className="w-px h-8 bg-border" />
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{summary.today}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Today</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="audit-filters">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search actions, IPs, entities..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
                data-testid="audit-search"
              />
            </div>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[170px]" data-testid="audit-action-filter">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map(a => (
                  <SelectItem key={a} value={a}>{formatAction(a)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="audit-entity-filter">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityTypes.map(e => (
                  <SelectItem key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              className="w-[150px]" placeholder="From"
              data-testid="audit-date-from"
            />
            <Input
              type="date" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              className="w-[150px]" placeholder="To"
              data-testid="audit-date-to"
            />
            {(search || actionFilter !== 'all' || entityFilter !== 'all' || dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={() => {
                setSearch(''); setActionFilter('all'); setEntityFilter('all');
                setDateFrom(''); setDateTo(''); setPage(0);
              }} data-testid="audit-clear-filters">
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Log Entries */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" />
              Activity Log
              <Badge variant="outline" className="ml-1">{total} events</Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No audit events found</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Actions will appear here as they happen</p>
            </div>
          ) : (
            <div className="space-y-0">
              {logs.map((log, i) => {
                const actionCfg = ACTION_ICONS[log.action] || { icon: Activity, color: 'text-gray-500', bg: 'bg-gray-500/10' };
                const ActionIcon = actionCfg.icon;
                const EntityIcon = ENTITY_ICONS[log.entity_type] || FileText;
                let ts;
                try { ts = format(parseISO(log.created_at), 'MMM d, yyyy HH:mm:ss'); } catch { ts = log.created_at; }

                return (
                  <div
                    key={log.id}
                    className={`flex items-start gap-4 py-3 px-2 transition-colors hover:bg-muted/30 ${
                      i < logs.length - 1 ? 'border-b border-border/50' : ''
                    }`}
                    data-testid={`audit-entry-${log.id}`}
                  >
                    {/* Icon */}
                    <div className={`h-9 w-9 rounded-lg ${actionCfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                      <ActionIcon className={`h-4 w-4 ${actionCfg.color}`} strokeWidth={1.8} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                          {formatAction(log.action)}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 gap-1">
                          <EntityIcon className="h-2.5 w-2.5" />
                          {log.entity_type}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground/60">{ts}</span>
                      </div>
                      <p className="text-sm mt-1 text-foreground/80">{log.details}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Globe2 className="h-3 w-3" /> {log.region}
                        </span>
                        <span className="flex items-center gap-1">
                          <Shield className="h-3 w-3" /> {log.ip_address}
                        </span>
                        {log.entity_id && (
                          <span className="flex items-center gap-1 font-mono text-[10px]">
                            ID: {log.entity_id.substring(0, 8)}...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-2">
              <p className="text-xs text-muted-foreground">
                Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm" disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="audit-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline" size="sm" disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="audit-next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
